import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { ArrowLeft, ChevronDown, ChevronUp, Pencil, Plus, Trash2, X } from 'lucide-react'
import { getEditor } from './registry'
import { emailTokens } from '../editable/formTokens'

/**
 * Éditeur de collection — la pop-in de TOUT champ déclaré `type: list`
 * (alias historique `stats-list`), au premier niveau comme en sous-éditeur
 * d'une colonne `list` imbriquée.
 *
 * DEUX VUES, et c'est ce qui règle son ergonomie :
 *
 *  - **Formulaire** dès qu'il n'y a qu'un élément — les champs les uns sous les
 *    autres, intitulé au-dessus, comme n'importe quel formulaire de
 *    l'application. Le cas d'un seul élément est le plus fréquent (une seule
 *    notification, un seul chiffre-clé) : lui imposer un tableau d'une ligne
 *    faisait payer à tout le monde la mise en page du cas rare.
 *  - **Tableau** dès qu'il y en a plusieurs — un résumé en LECTURE par ligne,
 *    et un bouton « Modifier » qui bascule sur le formulaire de cette
 *    ligne-là. C'est ce qui supprime la barre de défilement horizontale :
 *    l'ancienne version posait un contrôle de saisie par colonne ET par ligne,
 *    donc une grille qui ne tenait dans aucune largeur dès la troisième
 *    colonne.
 *
 * L'habillage vient du design system (`field-label`, `input`, `textarea`,
 * `table`, `btn`), comme les modales du builder — rien d'ad hoc ici.
 *
 * Props :
 *  - value (array)
 *  - columns?:   [{key, label, type, placeholder?, ...config}]
 *      Le `type` de chaque colonne est résolu dans le registre d'éditeurs
 *      (./registry.js) : un type `inline` (text, textarea, icon) se saisit
 *      directement, un type `modal` (image-drupal, link, list) ouvre son
 *      propre éditeur par-dessus.
 *  - addLabel?:  string   (libellé du bouton d'ajout, défaut « Ajouter un élément »)
 *  - defaultItem?: object (forme d'un élément neuf)
 *  - label?:     string   (intitulé du champ, pour les textes de navigation)
 *  - onSave, onClose
 *
 * Sans `columns:`, on retombe sur la forme {icon, value, label} d'origine, pour
 * que les consommateurs historiques (`editorType: stats-list`) continuent de
 * fonctionner sans être retouchés.
 */

const LEGACY_COLUMNS = [
  { key: 'icon', label: 'Icône', type: 'icon' },
  { key: 'value', label: 'Nombre', type: 'text', placeholder: 'ex : 20' },
  { key: 'label', label: 'Intitulé', type: 'text', placeholder: 'ex : partis membres' },
]
const LEGACY_DEFAULT_ITEM = { icon: 'Star', value: '', label: '' }

/** Longueur au-delà de laquelle un résumé de cellule est coupé. */
const SUMMARY_MAX = 70

/**
 * La liste que « @ » déroule dans UNE colonne.
 *
 * `tokens: form` déroule tout ce que le formulaire poste ; `tokens: email` s'en
 * tient aux champs qui portent une adresse — la colonne « destinataires » n'a
 * que faire d'un message, et une adresse invalide serait écartée en silence à
 * l'envoi. Une liste vide fait retomber la cellule sur son contrôle nu
 * (cf. `cells.jsx`) : une déclaration qui ne résout rien ne prive personne de
 * son champ de saisie.
 */
const tokensForColumn = (kind, tokens) => {
  const all = Array.isArray(tokens) ? tokens : []
  return kind === 'email' ? emailTokens(all) : all
}

/**
 * Le nom de l'élément, déduit du libellé du bouton d'ajout.
 *
 * « Ajouter un e-mail » → « e-mail » : les textes de navigation (« Retour aux
 * 3 e-mails ») parlent alors la langue du champ au lieu d'un « élément »
 * générique, sans rien demander de plus à la déclaration YAML.
 */
const nounFrom = (addLabel) => {
  if (typeof addLabel !== 'string') return 'élément'
  // L'alternance est ORDONNÉE : `un` avant `une` amputait « Ajouter une
  // dimension » en « e dimension » (d'où un « Retour aux 5 e dimensions »).
  // Les articles les plus longs passent donc en premier.
  const stripped = addLabel.replace(/^\s*ajouter\s+(une|des|les|un|le|la)?\s*/i, '').trim()
  return stripped || 'élément'
}

/** Résumé LISIBLE d'une cellule, pour la vue tableau (lecture seule). */
const summarize = (col, value) => {
  if (value == null || value === '') return null
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non'
  if (Array.isArray(value)) {
    return `${value.length} ${value.length > 1 ? 'éléments' : 'élément'}`
  }
  if (typeof value === 'object') {
    // Les valeurs composées du builder ont toutes une clé parlante : une image
    // porte son texte alternatif, un lien son adresse, un fond son type.
    const text = value.alt || value.url || value.label || value.title || value.type
    return text ? String(text) : 'défini'
  }
  const text = String(value).replace(/\s+/g, ' ').trim()
  if (!text) return null
  return text.length > SUMMARY_MAX ? `${text.slice(0, SUMMARY_MAX)}…` : text
}

export const CollectionEditor = ({ value, columns, addLabel, defaultItem, label, fieldTokens, onSave, onClose }) => {
  const cols = Array.isArray(columns) && columns.length > 0 ? columns : LEGACY_COLUMNS
  const blank = useMemo(() => defaultItem ?? (columns
    ? Object.fromEntries(cols.map(c => [c.key, getEditor(c.type)?.defaultValue ?? '']))
    : LEGACY_DEFAULT_ITEM), [defaultItem, columns, cols])

  const [items, setItems] = useState(
    Array.isArray(value) && value.length > 0
      ? value.map(i => ({ ...blank, ...i }))
      : [{ ...blank }],
  )

  // Index de la ligne ouverte en formulaire. `null` = vue tableau — qui n'a de
  // sens qu'à partir de deux éléments : en dessous, la vue formulaire est la
  // seule, et cet index est ignoré.
  const [editing, setEditing] = useState(0)
  // Sous-éditeur d'une colonne `modal` : {rowIdx, col}.
  const [sub, setSub] = useState(null)

  const fieldPrefix = useId()
  const formRef = useRef(null)
  // Vrai juste après un ajout ou un « Modifier » : le premier champ du
  // formulaire prend alors le focus, sinon l'utilisateur au clavier se
  // retrouve à naviguer une pop-in dont le contenu a changé sous lui.
  const focusNext = useRef(false)

  const single = items.length <= 1
  const showForm = single || editing !== null
  const rowIdx = single ? 0 : editing
  const noun = nounFrom(addLabel)

  useEffect(() => {
    if (!showForm || !focusNext.current) return
    focusNext.current = false
    const first = formRef.current?.querySelector('input, textarea, select, button')
    first?.focus?.()
  }, [showForm, rowIdx])

  const update = (idx, key, val) => {
    setItems(prev => prev.map((item, i) => (i === idx ? { ...item, [key]: val } : item)))
  }

  const add = () => {
    focusNext.current = true
    setItems(prev => {
      setEditing(prev.length)
      return [...prev, { ...blank }]
    })
  }

  const remove = (idx) => {
    setItems(prev => {
      const next = prev.filter((_, i) => i !== idx)
      // Jamais de vide absolu : une liste sans ligne n'offre plus rien à
      // remplir. On retombe sur un formulaire neuf — qui, laissé tel quel,
      // ne sera pas enregistré (voir `handleSave`).
      const result = next.length > 0 ? next : [{ ...blank }]
      setEditing(result.length > 1 ? null : 0)
      return result
    })
  }

  const move = (idx, dir) => {
    setItems(prev => {
      const target = idx + dir
      if (target < 0 || target >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })
  }

  const openRow = (idx) => {
    focusNext.current = true
    setEditing(idx)
  }

  const backToList = () => setEditing(null)

  const handleSave = () => {
    // Une ligne compte si AU MOINS UNE cellule porteuse de contenu est
    // remplie. Par défaut seules les cellules `inline` (texte, zone de texte,
    // icône) comptent : une cellule `modal` (image, lien, sous-liste) est un
    // accompagnement, sa présence seule ne fait pas une ligne.
    const meaningful = items.filter(it =>
      cols.some(c => {
        const plug = getEditor(c.type)
        const counts = plug?.isContentful ?? (plug?.mode === 'inline')
        if (!counts) return false
        const v = it[c.key]
        if (v == null) return false
        if (typeof v === 'string') return v.trim() !== ''
        return true
      }),
    )
    onSave(meaningful)
    onClose()
  }

  const saveSub = (val) => {
    update(sub.rowIdx, sub.col.key, val)
    setSub(null)
  }

  /**
   * Le contrôle d'une colonne, dans le formulaire d'une ligne.
   *
   * L'`id` est posé sur le contrôle LUI-MÊME (l'input d'une cellule `inline`,
   * le bouton d'une cellule `modal` — les deux sont étiquetables) : c'est ce
   * qui rend le `<label for>` opérant, donc le libellé cliquable et l'intitulé
   * annoncé par un lecteur d'écran.
   */
  const renderControl = (col, idx, id) => {
    const plug = getEditor(col.type)
    if (!plug) {
      console.warn(`[CollectionEditor] Type de colonne inconnu « ${col.type} »`)
      return <p className="field-hint">Type de champ inconnu : {col.type}</p>
    }
    const { Cell, mode } = plug
    if (!Cell) return <p className="field-hint">Ce type de champ n’a pas de contrôle.</p>
    // `key` est une donnée de colonne, pas une clé React : la retirer de
    // l'étalement évite l'avertissement « key in spread ». `label` est déjà
    // rendu par le `<label>` du formulaire.
    const { key: _ignored, label: _label, ...config } = col
    // `tokens:` déclare une cellule à INSERTION DE CHAMPS (« @ ») ; la liste,
    // elle, n'est pas dans le YAML — elle est dérivée du formulaire à
    // l'ouverture et arrive par `fieldTokens`. La déclaration ne dit donc que
    // « cette cellule accepte des champs », jamais lesquels.
    if (config.tokens) config.tokens = tokensForColumn(col.tokens, fieldTokens)
    return mode === 'inline'
      // Une cellule à pastilles n'est pas étiquetable par le `<label for>` du
      // formulaire (c'est un conteneur `contenteditable`) : elle reçoit
      // l'intitulé pour s'en faire un nom accessible.
      ? <Cell id={id} value={items[idx][col.key]} onChange={(v) => update(idx, col.key, v)} label={col.label} {...config} />
      : <Cell id={id} value={items[idx][col.key]} onClick={() => setSub({ rowIdx: idx, col })} {...config} />
  }

  const addButton = (
    <button type="button" onClick={add} className="builder-collection-add">
      <Plus size={15} aria-hidden="true" />
      {addLabel || 'Ajouter un élément'}
    </button>
  )

  return (
    <div className="builder-collection">
      {showForm ? (
        <>
          {/* Retour à la liste : n'existe que s'il Y A une liste derrière. */}
          {!single && (
            <div className="builder-collection-nav">
              <button type="button" onClick={backToList} className="builder-collection-back">
                <ArrowLeft size={14} aria-hidden="true" />
                Retour aux {items.length} {noun}s
              </button>
              <span className="field-hint">
                {rowIdx + 1} sur {items.length}
              </span>
            </div>
          )}

          <div className="builder-collection-form" ref={formRef}>
            {cols.map(col => (
              <div key={col.key} className="builder-collection-field">
                <label className="field-label" htmlFor={`${fieldPrefix}-${col.key}`}>
                  {col.label}
                </label>
                {renderControl(col, rowIdx, `${fieldPrefix}-${col.key}`)}
                {col.hint && <p className="field-hint">{col.hint}</p>}
              </div>
            ))}
          </div>

          <div className="builder-collection-formfoot">
            {addButton}
            <button
              type="button"
              onClick={() => remove(rowIdx)}
              className="btn btn-sm btn-ghost builder-editor-remove"
            >
              <Trash2 size={14} aria-hidden="true" />
              Supprimer
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="table-wrap builder-collection-tablewrap">
            <table className="table builder-collection-table">
              <caption className="sr-only">{label || 'Éléments de la liste'}</caption>
              <thead>
                <tr>
                  {cols.map(c => <th key={c.key} scope="col">{c.label}</th>)}
                  <th scope="col" className="builder-collection-actionscol">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx}>
                    {cols.map(c => {
                      const text = summarize(c, item[c.key])
                      return (
                        <td key={c.key} className="builder-collection-cell">
                          {text ?? <span className="builder-collection-blank">—</span>}
                        </td>
                      )
                    })}
                    <td className="builder-collection-rowactions">
                      {/* Le nom accessible reprend le texte visible et le
                          complète du rang : dix boutons « Modifier » dans une
                          liste ne se distinguent autrement que par le contexte
                          de leur ligne, qu'un lecteur d'écran ne redonne pas
                          quand on parcourt les contrôles. */}
                      <button
                        type="button"
                        onClick={() => openRow(idx)}
                        className="btn btn-sm btn-secondary"
                        aria-label={`Modifier la ligne ${idx + 1}`}
                      >
                        <Pencil size={13} aria-hidden="true" />
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="builder-collection-iconbtn"
                        aria-label={`Monter la ligne ${idx + 1}`}
                      >
                        <ChevronUp size={15} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, 1)}
                        disabled={idx === items.length - 1}
                        className="builder-collection-iconbtn"
                        aria-label={`Descendre la ligne ${idx + 1}`}
                      >
                        <ChevronDown size={15} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(idx)}
                        className="builder-collection-iconbtn builder-collection-iconbtn-danger"
                        aria-label={`Supprimer la ligne ${idx + 1}`}
                      >
                        <Trash2 size={15} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {addButton}
        </>
      )}

      <div className="builder-editor-actions">
        <button type="button" onClick={onClose} className="btn btn-secondary">Annuler</button>
        <button type="button" onClick={handleSave} className="btn btn-primary">Enregistrer</button>
      </div>

      {/* Sous-éditeur d'une colonne `modal` (image, lien, sous-liste) : il se
          pose PAR-DESSUS la pop-in plutôt que d'en ouvrir une seconde — deux
          dialogues empilés, l'un ancré et l'autre pas, ne se ferment jamais
          dans l'ordre où on les a ouverts. */}
      {sub && (() => {
        const plug = getEditor(sub.col.type)
        if (!plug?.Editor) return null
        const { Editor } = plug
        const isMedia = sub.col.type === 'image' || sub.col.type === 'image-drupal'
        const { key: _ignored, ...config } = sub.col
        return (
          <div className="builder-collection-sub">
            <div className="builder-collection-subhead">
              <span>{isMedia ? 'Médiathèque' : `Modifier — ${sub.col.label}`}</span>
              <button
                type="button"
                onClick={() => setSub(null)}
                className="builder-field-popover-close"
                aria-label="Fermer le sous-éditeur"
              >
                <X size={15} aria-hidden="true" />
              </button>
            </div>
            <div className="builder-collection-subbody">
              <Editor
                value={items[sub.rowIdx][sub.col.key]}
                label={sub.col.label}
                {...config}
                onSave={saveSub}
                onClose={() => setSub(null)}
              />
            </div>
          </div>
        )
      })()}
    </div>
  )
}
